import React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { CheckCircle2, ChevronDown, Film, ListChecks, Play } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { getHelpVideoPublicUrl } from '@/config/environment-config';
import { cn } from '@/lib/utils';
import { formatDuration, kindLabel } from './help-gallery';
import type { HelpEntry } from './help-types';

export interface HelpVideoListProps {
  entries: readonly HelpEntry[];
  watched: ReadonlySet<string>;
  /** The entry in the player now, marked "Playing". */
  currentId: string | null;
  onPlay: (entry: HelpEntry) => void;
  /** Accessible name of the list, e.g. "Clients videos". */
  label: string;
}

/**
 * Help entries as compact accordion rows (Help page): small poster, title, a Flow or Clip
 * badge, the length or "Coming soon", and a watched tick, with a Play button on the row.
 * Opening a row shows its Purpose. Each row carries the entry id, the target of deep links.
 */
export function HelpVideoList({ entries, watched, currentId, onPlay, label }: HelpVideoListProps) {
  return (
    <Accordion type="single" collapsible aria-label={label} className="divide-y divide-border rounded-xl border border-border bg-card">
      {entries.map((entry) => (
        <HelpVideoRow
          key={entry.id}
          entry={entry}
          watched={watched.has(entry.id)}
          playing={currentId === entry.id}
          onPlay={onPlay}
        />
      ))}
    </Accordion>
  );
}

function HelpVideoRow({
  entry,
  watched,
  playing,
  onPlay,
}: {
  entry: HelpEntry;
  watched: boolean;
  playing: boolean;
  onPlay: (entry: HelpEntry) => void;
}) {
  const poster = entry.poster ? getHelpVideoPublicUrl(entry.poster) : null;
  const published = entry.video !== null;
  const kind = kindLabel(entry);
  return (
    <AccordionItem
      value={entry.id}
      id={entry.id}
      data-testid={`help-row-${entry.id}`}
      className={cn('scroll-mt-24 border-b-0 px-2 sm:px-3', playing && 'bg-accent/50')}
    >
      <AccordionPrimitive.Header className="flex items-center gap-2">
        <AccordionPrimitive.Trigger
          className={cn(
            'group flex min-w-0 flex-1 items-center gap-3 rounded-lg py-2 text-left',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <span
            className={cn(
              'relative block aspect-video w-20 shrink-0 overflow-hidden rounded-md border bg-muted sm:w-24',
              published ? 'border-border' : 'border-dashed border-border/80 bg-muted/50',
            )}
          >
            {poster ? (
              <img src={poster} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-muted-foreground/70">
                <Film className="h-4 w-4" aria-hidden />
              </span>
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className={cn('line-clamp-2 text-sm font-medium leading-snug', published ? 'text-foreground' : 'text-muted-foreground')}>
              {entry.title}
            </span>
            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              {kind !== 'Task' && (
                <span
                  className={cn(
                    'rounded px-1.5 py-px text-[11px] font-semibold uppercase tracking-wide',
                    kind === 'Flow' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
                  )}
                >
                  {kind}
                </span>
              )}
              {published ? (
                entry.durationSec !== null && <span className="tabular-nums">{formatDuration(entry.durationSec)}</span>
              ) : (
                <span>Coming soon</span>
              )}
              {playing && <span className="font-semibold text-primary">Playing</span>}
            </span>
          </span>
          {watched && <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" aria-label="Watched" role="img" />}
          <ChevronDown
            className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
            aria-hidden
          />
        </AccordionPrimitive.Trigger>
        <Button
          size="icon"
          variant={playing ? 'default' : 'ghost'}
          className="h-9 w-9 shrink-0 rounded-full"
          onClick={() => onPlay(entry)}
          aria-label={published ? `Play ${entry.title}` : `Show the steps for ${entry.title}`}
        >
          {published ? <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden /> : <ListChecks className="h-4 w-4" aria-hidden />}
        </Button>
      </AccordionPrimitive.Header>
      <AccordionContent className="space-y-2 pb-3 pl-[5.75rem] pr-11 sm:pl-[6.75rem]">
        {entry.purpose && <p className="leading-relaxed text-muted-foreground">{entry.purpose}</p>}
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onPlay(entry)}>
          {published ? <Play className="h-3.5 w-3.5 fill-current" aria-hidden /> : <ListChecks className="h-3.5 w-3.5" aria-hidden />}
          {published ? `Play${entry.durationSec !== null ? ` (${formatDuration(entry.durationSec)})` : ''}` : 'Show the steps'}
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
}
